import Chart from 'highcharts-react-official';
import { CategorySlotCount, SlotCategory } from 'types/evaluation';
import noData from 'highcharts/modules/no-data-to-display';
import Highcharts from 'highcharts';
import { formatNumber, format0 } from 'utils/number';
import { parseMomentUtc } from 'utils/dates';
import { AggregationPeriod } from 'types/misc';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
// Deep imports (not the barrel) so this chart doesn't drag in the SciChart exporter /
// html-to-image / the options drawer — it only needs the Highcharts exporter + presets.
import { createHighchartsExporter } from 'components/chart-export/highcharts-exporter';
import { GENERIC_CHART_ELEMENTS } from 'components/chart-export/chart-element-presets';
import type { ChartImageExporter } from 'components/chart-export/types';
import style from './rescat-slot-count-chart.module.scss';
import classNames from 'classnames';
import { categoryColors, categoryLabels, xAxisTickInterval } from './utils';
import { isNil } from 'lodash-es';
import {
  shownSlotCategoriesIn,
  slotCategoryRecord,
} from 'utils/slot-categories';

interface RescatSlotCountChartProps {
  slotCount: CategorySlotCount[];
  interval: AggregationPeriod;
  isLoading: boolean;
  /**
   * Opt-in image-export seam: called with a ready-to-use image exporter for this chart (and
   * `null` on unmount / when it rebuilds). Absent by default, so the legacy
   * aggregated-free-cash-flow consumer is untouched; eval3-new passes a callback that
   * registers the chart for content export (captured offscreen from its options, so it
   * exports even while the section is hidden).
   */
  onExporterReady?: (exporter: ChartImageExporter | null) => void;
}

noData(Highcharts);

/**
 * @param {CategorySlotCount} item - One interval's count.
 * @return {string} Its category.
 */
const rescatCategory = (item: CategorySlotCount) => item.rollForwardSlotCat;
/**
 * @param {CategorySlotCount} item - One interval's count.
 * @return {number} Its slot count.
 */
const rescatCount = (item: CategorySlotCount) => item.slotCount;

export const RescatSlotCountChart = memo((props: RescatSlotCountChartProps) => {
  const { slotCount, interval, isLoading, onExporterReady } = props;
  const chartRef = useRef<{ chart: Highcharts.Chart }>(null);

  // Copy before sorting — `.sort()` mutates in place, and the caller may pass a
  // memoized/cached array (the eval3-new section memoizes it).
  const sortedSlotCount = [...slotCount].sort((a, b) => {
    return parseMomentUtc(a.date).diff(parseMomentUtc(b.date));
  });

  // A Create New Well status gets a series and legend entry only with slots in it (INS-13303).
  const shownCategories = useMemo(
    () => shownSlotCategoriesIn(slotCount, rescatCategory, rescatCount),
    [slotCount]
  );

  const [slotCategoryVisibility, setSlotCategoryVisibility] = useState<
    Record<SlotCategory, boolean>
  >(() => slotCategoryRecord(() => true));

  useEffect(() => {
    if (chartRef.current) {
      if (isLoading) {
        chartRef.current.chart.showLoading();
      } else {
        chartRef.current.chart.hideLoading();
      }
    }
  }, [isLoading]);

  // Build an image exporter for this chart ONLY when a consumer opts in — the legacy
  // aggregated-free-cash-flow consumer passes no callback, so this stays inert there.
  // Rebuilt when the legend visibility changes so the export-only legend's checked flags +
  // the exported series match the on-screen chart.
  const exporter = useMemo<ChartImageExporter | null>(() => {
    if (!onExporterReady) {
      return null;
    }
    return createHighchartsExporter({
      getChart: () => chartRef.current?.chart ?? null,
      elementHooks: { getElements: () => GENERIC_CHART_ELEMENTS },
      // Drop categories toggled off in the custom legend so the export matches the chart.
      filterSeries: (series) =>
        slotCategoryVisibility[series.name as SlotCategory] !== false,
      // The on-screen legend is a React component (the Highcharts legend is disabled), so
      // give the exported image its own legend from the category colors/labels + visibility.
      legendItems: shownCategories.map((category) => ({
        label: categoryLabels[category],
        color: categoryColors[category],
        checked: slotCategoryVisibility[category],
      })),
    });
  }, [onExporterReady, slotCategoryVisibility, shownCategories]);

  // Push the exporter to the consumer on ready/rebuild, and null it on unmount (opt-in seam).
  useEffect(() => {
    onExporterReady?.(exporter);
    return () => onExporterReady?.(null);
  }, [onExporterReady, exporter]);

  return (
    <>
      <Chart
        highcharts={Highcharts}
        ref={chartRef}
        options={{
          credits: {
            enabled: false,
          },
          // Importing the Highcharts image-exporter (for the opt-in content-export seam)
          // registers the `exporting` module, which otherwise renders its default context
          // (hamburger) button. Disable that button so this chart looks exactly as before on
          // every consumer (legacy + eval3-new) — the export API stays available (offscreen
          // capture uses it directly, not this button).
          exporting: {
            buttons: {
              contextButton: {
                enabled: false,
              },
            },
          },
          chart: {
            marginRight: 100,
            marginLeft: 100,
          },
          noData: {
            position: {
              align: 'center',
              verticalAlign: 'middle',
            },
          },
          lang: {
            noData: 'No data to display',
          },
          tooltip: {
            formatter: function (
              this: Highcharts.TooltipFormatterContextObject
            ) {
              let slotPercentage = '';
              if (
                !isNil(this.y) &&
                !isNil(this.total) &&
                Number.isFinite(this.y / this.total)
              ) {
                slotPercentage = formatNumber(
                  (this.y / this.total) * 100,
                  2,
                  2
                );
              }
              return `<b>${format0(this.y)}</b> Slots${
                slotPercentage
                  ? `<br/><b>${slotPercentage}%</b> ${
                      categoryLabels[this.series.name as SlotCategory]
                    }`
                  : ''
              }`;
            },
          },
          plotOptions: {
            column: {
              stacking: 'normal',
            },
          },
          legend: {
            enabled: false,
          },
          title: {
            text: 'Roll Forward Slots',
            align: 'center',
          },
          xAxis: {
            type: 'datetime',
            dateTimeLabelFormats: {
              day: '%m/%d/%Y',
              week: '%m/%d/%Y',
              month: '%m/%d/%Y',
              year: '%m/%d/%Y',
            },
            tickLength: 0,
            tickInterval: xAxisTickInterval[interval].tickInterval,
            labels: {
              useHTML: true,
              y: 35,
              align: 'center',
              style: {
                color: '#000',
                fontWeight: 'bold',
              },
            },
          },
          yAxis: {
            title: {
              text: 'Number of Slots',
              style: {
                fontSize: '12px',
                fontWeight: 'bold',
                color: '#000',
              },
            },
            stackLabels: {
              enabled: false,
            },
          },
          // One column series per category, LTD on top and PDP at the base. A row whose category
          // is not in the shared list (an old eval's grouped bucket) has no series.
          series: [...shownCategories].reverse().map((category) => ({
            visible: slotCategoryVisibility[category],
            type: 'column' as const,
            name: category,
            data: sortedSlotCount
              .filter((item) => item.rollForwardSlotCat === category)
              .map((item) => [
                parseMomentUtc(item.date).valueOf(),
                item.slotCount,
              ]),
            color: categoryColors[category],
          })),
        }}
      ></Chart>
      <div className={style.legendContainer}>
        {shownCategories.map((category) => {
          return (
            <button
              key={category}
              onClick={() => {
                setSlotCategoryVisibility((prev) => ({
                  ...prev,
                  [category]: !prev[category],
                }));
              }}
              className={style.legend}
            >
              <span
                className={style.circle}
                style={{
                  backgroundColor: !slotCategoryVisibility[category]
                    ? 'gray'
                    : categoryColors[category],
                }}
              />
              <p
                className={classNames(style.legendLabel, {
                  [style.notVisible]: !slotCategoryVisibility[category],
                })}
              >
                {categoryLabels[category as SlotCategory]}
              </p>
            </button>
          );
        })}
      </div>
    </>
  );
});
RescatSlotCountChart.displayName = 'RescatSlotCountChart';
